import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  BarChart3, 
  Search, 
  GitBranch, 
  TrendingUp,
  Target,
  Gauge,
  FileSearch,
  Layers,
  CheckCircle2
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const workstreams = [
  {
    number: '01',
    icon: Gauge,
    title: 'Utility Assessment',
    subtitle: 'Mapping the Impact Curve',
    description: 'We analyze the relationship between financial inputs and health outcomes to determine where your resources are achieving the highest marginal impact.',
    items: [
      {
        title: 'Efficiency Identification',
        text: 'Pinpointing interventions that provide the greatest "health-per-dollar" return.'
      },
      {
        title: 'Waste Mitigation',
        text: 'Identifying programs that have reached a point of diminishing returns or "Dead-Weight Loss."'
      }
    ]
  },
  {
    number: '02',
    icon: Search,
    title: 'Bottleneck Diagnostics',
    subtitle: 'Identifying Leapfrog Potential',
    description: 'We don\'t just find problems; we find opportunities for Systemic Leapfrogging. Our diagnostics pinpoint exactly where "Default" legacy systems are causing systemic stagnation.',
    items: [
      {
        title: 'Readiness Assessment',
        text: 'Evaluating the system\'s capacity to adopt AI and digital governance.'
      },
      {
        title: 'Architecture of Change',
        text: 'Providing the blueprint to bypass bottlenecks through intentional design.'
      }
    ]
  },
  {
    number: '03',
    icon: GitBranch,
    title: 'Path-Dependent Analysis',
    subtitle: 'Evidence-Based Approach',
    description: 'Health systems are complex adaptive environments where one size never fits all. Using advanced analytical methods, we audit the "Causal Recipes" that lead to success.',
    items: [
      {
        title: 'Complex Causality',
        text: 'Understanding how combinations of leadership, policy, and funding interact.'
      },
      {
        title: 'Local Nuance',
        text: 'Ensuring lessons are effectively translated without losing deep local context.'
      },
      {
        title: 'Set-Theoretic M&E',
        text: 'Moving beyond "averages" to find multiple pathways to high-impact results.'
      }
    ]
  }
];

const auditAdvantages = [
  {
    icon: Layers,
    title: 'Strategic Learning Systems',
    description: 'M&E frameworks that evolve with your project.'
  },
  {
    icon: CheckCircle2,
    title: 'Evidence-based Confidence',
    description: 'For donors and governments to scale successful pilots.'
  },
  {
    icon: BarChart3,
    title: 'Marginal Utility Reports',
    description: 'Clear visualizations of where investments are most "active" in saving lives.'
  }
];

export const TechnicalPillarsPage = () => {
  return (
    <div data-testid="technical-pillars-page">
      {/* Hero Section */}
      <section className="section-padding pt-32" data-testid="technical-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Technical Pillars
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Beyond the Box-Ticking: Auditing for Marginal Utility
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              Monitoring and Evaluation (M&E) is often treated as a retrospective compliance 
              exercise. At HSL, we redefine M&E as a forward-looking tool for systemic 
              transformation. Our <span className="text-teal-600 font-semibold">Strategic Health 
              Audits</span> are designed to identify the "Marginal Utility" of investments—ensuring 
              that every unit of capital, effort, and time is architected for maximum health gain.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section-padding bg-slate-50" data-testid="methodology-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Our Proprietary Methodology
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              Three High-Impact Analytical Workstreams
            </h2>
            <p className="text-slate-600 text-lg">
              We move beyond linear reporting to embrace the complexity of health systems.
            </p>
          </motion.div>

          {/* Workstreams */}
          <div className="space-y-8">
            {workstreams.map((workstream, index) => (
              <motion.div
                key={workstream.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors"
                data-testid={`workstream-${index}`}
              >
                <div className="grid lg:grid-cols-12 gap-0">
                  {/* Number Column */}
                  <div className="lg:col-span-2 bg-slate-100/30 p-8 flex items-center justify-center">
                    <span className="font-heading text-6xl lg:text-7xl font-bold text-slate-700">
                      {workstream.number}
                    </span>
                  </div>
                  
                  {/* Content Column */}
                  <div className="lg:col-span-10 p-8 lg:p-10">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0">
                        <workstream.icon size={24} className="text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-heading text-2xl text-slate-900">{workstream.title}</h3>
                        <span className="text-teal-600 text-sm">{workstream.subtitle}</span>
                      </div>
                    </div>
                    
                    <p className="text-slate-500 leading-relaxed mb-6">
                      {workstream.description}
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {workstream.items.map((item, i) => (
                        <div key={i} className="bg-slate-100/30 p-4">
                          <h4 className="text-slate-900 font-medium mb-2 text-sm">{item.title}</h4>
                          <p className="text-slate-500 text-sm">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audit Advantage Section */}
      <section className="section-padding" data-testid="audit-advantage">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                The HSL Audit Advantage
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Proof of Concept for High-Stakes Decisions
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                An HSL Strategic Health Audit provides the "Proof of Concept" required for 
                high-stakes decision-making. We deliver actionable intelligence that transforms 
                data into strategic advantage.
              </p>
              
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-300 font-medium transition-colors group"
                data-testid="audit-cta"
              >
                Request an Audit Consultation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              {auditAdvantages.map((advantage, index) => (
                <motion.div
                  key={advantage.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-slate-50 border border-slate-200 p-6 hover:border-teal-200 transition-colors group"
                  data-testid={`advantage-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <advantage.icon size={24} className="text-slate-500 group-hover:text-teal-600 transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-slate-900 mb-2">{advantage.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{advantage.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visual Data Section */}
      <section className="section-padding bg-slate-50" data-testid="data-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-slate-200 p-8 lg:p-12"
          >
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Utility Curve Visualization */}
              <div className="text-center">
                <div className="h-40 flex items-end justify-center gap-2 mb-4">
                  {[30, 50, 70, 85, 90, 88, 82].map((height, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${height}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className={`w-6 ${i < 4 ? 'bg-teal-400' : 'bg-slate-600'}`}
                    />
                  ))}
                </div>
                <h4 className="text-slate-900 font-medium mb-1">Marginal Utility Curve</h4>
                <p className="text-slate-500 text-sm">Identifying peak efficiency points</p>
              </div>

              {/* Bottleneck Heatmap */}
              <div className="text-center">
                <div className="h-40 grid grid-cols-4 grid-rows-4 gap-1 mb-4">
                  {[...Array(16)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className={`${
                        [3, 7, 11, 6, 10].includes(i) 
                          ? 'bg-teal-400' 
                          : [2, 5, 9, 14].includes(i)
                          ? 'bg-teal-600'
                          : 'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
                <h4 className="text-slate-900 font-medium mb-1">Bottleneck Heatmap</h4>
                <p className="text-slate-500 text-sm">System-wide diagnostic view</p>
              </div>

              {/* Set Analysis Venn */}
              <div className="text-center">
                <div className="h-40 relative flex items-center justify-center mb-4">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="absolute w-24 h-24 rounded-full bg-teal-400 -translate-x-4"
                  />
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="absolute w-24 h-24 rounded-full bg-slate-400 translate-x-4"
                  />
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="absolute w-24 h-24 rounded-full bg-teal-600 translate-y-4"
                  />
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="absolute w-8 h-8 rounded-full bg-white"
                  />
                </div>
                <h4 className="text-slate-900 font-medium mb-1">Set Analysis</h4>
                <p className="text-slate-500 text-sm">Identifying causal intersections</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="technical-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Transform Your M&E Approach
            </h2>
            <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
              Move beyond compliance to strategic intelligence with HSL's Technical Pillars.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center justify-center gap-2 group"
                data-testid="technical-cta-primary"
              >
                Request an Audit
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="technical-cta-secondary"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
